import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import TrustCardPreview from "@/components/TrustCardPreview";
import TrustCheckPanel from "@/components/TrustCheckPanel";
import ShopPreview from "@/components/ShopPreview";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Download, Printer, Wallet, Smartphone, ShieldCheck, RefreshCcw, Lock, Unlock, Info, Lock as LockIcon, ListIcon, AlertTriangle, Bell, BellOff, ShieldAlert, Users as UsersIcon, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import TrustHint from "@/components/TrustHint";

const GROUPS = ["medical", "contacts", "documents", "notes"];

export default function TrustCardPage() {
  const { t, lang } = useI18n();
  const [card, setCard] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [data, setData] = useState({});
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [qrSrc, setQrSrc] = useState("");
  const [dataEditor, setDataEditor] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [notif, setNotif] = useState(null);
  const [showAllLog, setShowAllLog] = useState(false);

  const refreshQr = () => setQrSrc(`${API_BASE}/trust-card/qr.png?ts=${Date.now()}`);

  const load = async () => {
    try {
      const { data: payload } = await api.get("/trust-card");
      setCard(payload.card);
      setPermissions(payload.permissions);
      setData(payload.data || {});
      refreshQr();
    } catch (e) { /* ignore */ }
    try {
      const { data: logs } = await api.get("/trust-card/access-log");
      setLog(logs);
    } catch (e) { /* ignore */ }
    try {
      const { data: n } = await api.get("/trust-card/notification-settings");
      setNotif(n);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const updateNotif = async (patch) => {
    const next = { ...notif, ...patch };
    setNotif(next);
    try {
      const { data } = await api.put("/trust-card/notification-settings", next);
      setNotif(data);
    } catch {
      toast.error("Konnte Einstellungen nicht speichern");
      load();
    }
  };

  const updatePerm = async (next) => {
    setPermissions(next);
    try { await api.put("/trust-card/permissions", next); }
    catch { toast.error("Konnte Freigabe nicht speichern"); load(); }
  };

  const toggleItem = (group, key) => {
    if (!permissions) return;
    const next = { ...permissions, [group]: { ...permissions[group], [key]: !permissions[group][key] } };
    updatePerm(next);
  };

  const setAccessMode = (mode) => {
    if (!permissions) return;
    updatePerm({ ...permissions, access_mode: mode });
  };

  const saveData = async () => {
    try {
      await api.put("/trust-card/data", data);
      toast.success("Notfallinformationen gespeichert.");
      setDataEditor(false);
    } catch { toast.error("Konnte nicht speichern"); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post("/trust-card/regenerate");
      toast.success(t.trustCard.security.regenerated);
      setRegenOpen(false);
      await load();
    } catch { toast.error("Konnte nicht erneuern"); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      await api.post(card?.active ? "/trust-card/lock" : "/trust-card/unlock");
      toast.success(card?.active ? "Karte gesperrt." : "Karte aktiviert.");
      await load();
    } catch { toast.error("Konnte Status nicht ändern"); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    // Use a hidden anchor to trigger withCredentials download
    fetch(`${API_BASE}/trust-card/pdf`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Vertrauenskarte-${card?.notfall_id || "card"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("PDF-Download fehlgeschlagen"));
  };

  const printCard = () => {
    document.body.classList.add("ag-print-card");
    window.print();
    setTimeout(() => document.body.classList.remove("ag-print-card"), 500);
  };

  if (!card || !permissions) {
    return (
      <AppLayout>
        <div className="text-slate-500 text-sm">{t.common.loading}</div>
      </AppLayout>
    );
  }

  const itemsByGroup = {
    medical: ["blood_type", "allergies", "medications", "hausarzt", "krankenkasse", "organ_donor"],
    contacts: ["partner", "kinder", "eltern", "notfallkontakte"],
    documents: ["patientenverfuegung", "vorsorgevollmacht", "betreuungsverfuegung", "notfallmappe"],
    notes: ["pets", "care_notes", "important_info"],
  };

  return (
    <AppLayout>
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 inline-flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> {t.nav.trustCard}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ag-ink leading-[1.05] mb-3">{t.trustCard.title}</h1>
        <p className="text-slate-600 max-w-2xl">{t.trustCard.subtitle}</p>
        <div className="mt-5">
          <TrustHint testId="trustcard-trust-hint">{t.trustHints.trustCard}</TrustHint>
        </div>
      </header>

      <section className="grid lg:grid-cols-[1fr_1fr] gap-8 items-start mb-12">
        <div className="flex flex-col items-center lg:items-start ag-fade-in">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">{t.trustCard.preview}</div>
          <TrustCardPreview
            name={card.name}
            notfallId={card.notfall_id}
            qrSrc={qrSrc}
            label={t.trustCard.cardLabel}
            scanHint={t.trustCard.scanHint}
          />
          {!card.active && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg" data-testid="trust-card-locked-banner">
              <LockIcon className="h-3 w-3" /> {t.trustCard.security.locked}
            </div>
          )}
        </div>

        <div className="space-y-3 ag-fade-in" style={{ animationDelay: "80ms" }}>
          <Button onClick={downloadPdf} data-testid="trust-card-pdf" className="w-full justify-start h-12 bg-ag-ink hover:bg-slate-800 rounded-xl">
            <Download className="h-4 w-4 mr-3" /> {t.trustCard.actions.pdf}
          </Button>
          <Button onClick={printCard} variant="outline" data-testid="trust-card-print" className="w-full justify-start h-12 rounded-xl border-slate-200">
            <Printer className="h-4 w-4 mr-3" /> {t.trustCard.actions.print}
          </Button>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" data-testid="trust-card-apple" onClick={() => toast(t.trustCard.actions.soon)} className="h-11 rounded-xl border-slate-200 text-xs">
              <Wallet className="h-4 w-4 mr-1.5" /> Apple
            </Button>
            <Button variant="outline" data-testid="trust-card-google" onClick={() => toast(t.trustCard.actions.soon)} className="h-11 rounded-xl border-slate-200 text-xs">
              <Wallet className="h-4 w-4 mr-1.5" /> Google
            </Button>
            <Button variant="outline" data-testid="trust-card-nfc" onClick={() => toast(t.trustCard.actions.soon)} className="h-11 rounded-xl border-slate-200 text-xs">
              <Smartphone className="h-4 w-4 mr-1.5" /> NFC
            </Button>
          </div>

          <div className="ag-card p-5 mt-3 border-amber-200/60 bg-amber-50/40">
            <div className="flex items-start gap-3">
              <Info className="h-4 w-4 text-amber-700 mt-0.5" />
              <div className="text-xs text-slate-700">
                {t.trustCard.security.blockedItems}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vertrauens-Check + Systemstatus */}
      <TrustCheckPanel onChanged={load} />

      {/* Permissions */}
      <section className="ag-card p-7 mb-8" data-testid="trust-card-permissions">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif text-3xl text-ag-ink">{t.trustCard.permissions.title}</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">{t.trustCard.permissions.sub}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {GROUPS.map((group) => (
            <div key={group} className="rounded-2xl border border-slate-100 p-5">
              <div className="font-serif text-xl text-ag-ink mb-3">{t.trustCard.permissions.groups[group]}</div>
              <ul className="divide-y divide-slate-100">
                {itemsByGroup[group].map((key) => (
                  <li key={key} className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-slate-700">{t.trustCard.permissions.items[key]}</span>
                    <Switch
                      checked={!!permissions[group]?.[key]}
                      onCheckedChange={() => toggleItem(group, key)}
                      data-testid={`perm-${group}-${key}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-6">
          <div className="text-sm font-medium text-ag-ink mb-3">{t.trustCard.permissions.accessMode.title}</div>
          <RadioGroup value={permissions.access_mode} onValueChange={setAccessMode} data-testid="perm-access-mode" className="grid sm:grid-cols-2 gap-3">
            {["qr_only", "verified", "named_only", "never"].map((mode) => (
              <label key={mode} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${permissions.access_mode === mode ? "border-ag-ink bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
                <RadioGroupItem value={mode} className="mt-0.5" data-testid={`access-mode-${mode}`} />
                <span className="text-sm text-slate-700">{t.trustCard.permissions.accessMode[mode]}</span>
              </label>
            ))}
          </RadioGroup>
        </div>
      </section>

      {/* Data editor */}
      <section className="ag-card p-7 mb-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-serif text-3xl text-ag-ink">{t.trustCard.content.title}</h2>
            <p className="text-sm text-slate-600 mt-1">{t.trustCard.content.sub}</p>
          </div>
          {!dataEditor && (
            <Button variant="outline" onClick={() => setDataEditor(true)} data-testid="trust-card-edit-data" className="rounded-xl">
              Bearbeiten
            </Button>
          )}
        </div>
        {!dataEditor ? (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {Object.entries(t.trustCard.content.fields).map(([key, label]) => (
              <div key={key} className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-50">
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-ag-ink text-right max-w-[60%] truncate">
                  {key === "organ_donor"
                    ? data[key] ? "Ja" : "—"
                    : (data[key] || "—")}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(t.trustCard.content.fields).map(([key, label]) => {
              if (key === "organ_donor") {
                return (
                  <div key={key} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2">
                    <Label>{label}</Label>
                    <Switch checked={!!data.organ_donor} onCheckedChange={(v) => setData({ ...data, organ_donor: v })} data-testid="data-organ-donor" />
                  </div>
                );
              }
              const long = ["care_notes", "important_info", "allergies", "medications"].includes(key);
              return (
                <div key={key} className={long ? "sm:col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
                  {long ? (
                    <Textarea
                      rows={2}
                      value={data[key] || ""}
                      onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      data-testid={`data-${key}`}
                      className="mt-1.5"
                    />
                  ) : (
                    <Input
                      value={data[key] || ""}
                      onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      data-testid={`data-${key}`}
                      className="mt-1.5 h-11"
                    />
                  )}
                </div>
              );
            })}
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setDataEditor(false); load(); }}>{t.common.cancel}</Button>
              <Button onClick={saveData} className="bg-ag-ink hover:bg-slate-800" data-testid="trust-card-save-data">{t.trustCard.content.save}</Button>
            </div>
          </div>
        )}
      </section>

      {/* Security + Log */}
      <section className="grid lg:grid-cols-2 gap-6 mb-12">
        <div className="ag-card p-7">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4 text-slate-500" /><h2 className="font-serif text-2xl text-ag-ink">{t.trustCard.security.title}</h2></div>
          <p className="text-sm text-slate-600 mb-5">{card.active ? t.trustCard.security.active : t.trustCard.security.locked}</p>
          <div className="space-y-3">
            <Button onClick={() => setRegenOpen(true)} disabled={busy} data-testid="trust-card-regenerate" className="w-full justify-start h-11 bg-amber-600 hover:bg-amber-700 rounded-xl">
              <RefreshCcw className="h-4 w-4 mr-2" /> {t.trustCard.security.regenerate}
            </Button>
            <Button onClick={toggleLock} disabled={busy} variant="outline" data-testid="trust-card-lock" className="w-full justify-start h-11 rounded-xl border-slate-200">
              {card.active ? <Lock className="h-4 w-4 mr-2" /> : <Unlock className="h-4 w-4 mr-2" />}
              {card.active ? t.trustCard.security.lock : t.trustCard.security.unlock}
            </Button>
          </div>
        </div>

        <div className="ag-card p-7" data-testid="trust-card-access-log">
          <div className="flex items-center gap-2 mb-2"><ListIcon className="h-4 w-4 text-slate-500" /><h2 className="font-serif text-2xl text-ag-ink">{t.trustCard.log.title}</h2></div>
          <p className="text-sm text-slate-600 mb-4">{t.trustCard.log.sub}</p>
          {log.length === 0 ? (
            <div className="text-sm text-slate-500">{t.trustCard.log.empty}</div>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 text-sm">
                {(showAllLog ? log : log.slice(0, 5)).map((e) => (
                  <li key={e.id} className="py-3" data-testid={`log-entry-${e.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-ag-ink font-medium">{new Date(e.ts).toLocaleString(lang)}</span>
                          {e.success ? (
                            <span className="ag-badge ag-badge-green"><CheckCircle2 className="h-3 w-3" /> {t.trustCard.log.statusOk}</span>
                          ) : (
                            <span className="ag-badge ag-badge-red"><XCircle className="h-3 w-3" /> {t.trustCard.log.statusError}{e.error_code ? ` · ${e.error_code}` : ""}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span>{e.device || "—"}</span>
                          {e.browser && <span>· {e.browser}</span>}
                          {e.os && <span>· {e.os}</span>}
                          {e.country && <span>· {e.country}</span>}
                          <span className="font-mono">· {e.ip || "—"}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {log.length > 5 && (
                <button
                  onClick={() => setShowAllLog((v) => !v)}
                  data-testid="log-toggle"
                  className="text-xs text-ag-ink hover:underline mt-4"
                >
                  {showAllLog ? t.trustCard.log.showLess : `${t.trustCard.log.showAll} (${log.length})`}
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Notifications */}
      {notif && (
        <section className="ag-card p-7 mb-12" data-testid="notifications-section">
          <div className="flex items-center gap-2 mb-1"><Bell className="h-4 w-4 text-slate-500" /><h2 className="font-serif text-3xl text-ag-ink">{t.trustCard.notifications.title}</h2></div>
          <p className="text-sm text-slate-600 mb-5 max-w-xl">{t.trustCard.notifications.sub}</p>

          <div className="grid md:grid-cols-2 gap-3">
            {[
              { k: "email", available: true },
              { k: "push", available: false },
              { k: "sms", available: false },
              { k: "whatsapp", available: false },
              { k: "wallet", available: false },
              { k: "smartwatch", available: false },
            ].map(({ k, available }) => (
              <label
                key={k}
                className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${available ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/60"}`}
              >
                <div className="flex items-center gap-3">
                  {k === "email" ? <Bell className="h-4 w-4 text-slate-500" /> : <BellOff className="h-4 w-4 text-slate-400" />}
                  <div>
                    <div className="text-sm text-ag-ink">{t.trustCard.notifications.channels[k]}</div>
                    {!available && <div className="text-[10px] uppercase tracking-wider text-amber-600">{t.trustCard.notifications.soon}</div>}
                  </div>
                </div>
                <Switch
                  checked={!!notif[k]}
                  disabled={!available || notif.none}
                  onCheckedChange={(v) => updateNotif({ [k]: v, ...(v ? { none: false } : {}) })}
                  data-testid={`notif-${k}`}
                />
              </label>
            ))}
            <label className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/40 px-4 py-3 md:col-span-2">
              <div className="flex items-center gap-3">
                <BellOff className="h-4 w-4 text-slate-500" />
                <div className="text-sm text-ag-ink">{t.trustCard.notifications.channels.none}</div>
              </div>
              <Switch
                checked={!!notif.none}
                onCheckedChange={(v) => updateNotif({ none: v, ...(v ? { email: false, push: false, sms: false, whatsapp: false, wallet: false, smartwatch: false } : { email: true }) })}
                data-testid="notif-none"
              />
            </label>
          </div>

          <div className="mt-6 border-t border-slate-100 pt-5 grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 rounded-2xl border border-slate-100 p-4">
              <UsersIcon className="h-4 w-4 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-ag-ink">{t.trustCard.notifications.trustedContacts}</div>
                  <Switch
                    checked={!!notif.notify_trusted_contacts}
                    onCheckedChange={(v) => updateNotif({ notify_trusted_contacts: v })}
                    data-testid="notif-trusted"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1">{t.trustCard.notifications.trustedContactsSub}</div>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-2xl border border-slate-100 p-4">
              <ShieldAlert className="h-4 w-4 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-ag-ink">{t.trustCard.notifications.autoLock}</div>
                  <Switch
                    checked={!!notif.auto_lock_enabled}
                    onCheckedChange={(v) => updateNotif({ auto_lock_enabled: v })}
                    data-testid="notif-autolock"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1">{t.trustCard.notifications.autoLockSub}</div>
                {notif.auto_lock_enabled && (
                  <div className="flex items-center gap-3 mt-3">
                    <label className="text-xs text-slate-600">{t.trustCard.notifications.threshold}</label>
                    <Input
                      type="number" min={2} max={50}
                      value={notif.auto_lock_threshold}
                      onChange={(e) => updateNotif({ auto_lock_threshold: Math.max(2, Number(e.target.value) || 5) })}
                      data-testid="notif-threshold"
                      className="h-8 w-20 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Shop (Architektur-Vorbereitung, V2) */}
      <ShopPreview />

      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> {t.trustCard.security.regenerate}
            </DialogTitle>
            <DialogDescription>{t.trustCard.security.regenerateHint}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRegenOpen(false)}>{t.common.cancel}</Button>
            <Button onClick={regenerate} disabled={busy} data-testid="trust-card-regenerate-confirm" className="bg-amber-600 hover:bg-amber-700">{t.trustCard.security.regenerate}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
